import { theme } from 'antd';
import { getDownloadURL } from 'library/firebase/fb.utils';
import SuperFetch from 'library/helpers/superFetch';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { inventoryActions } from 'reduxStore/inventory/slice';
import { LeafletMap, Loader, Notification } from 'zComponents/atoms';
import { Avatar, Button, Empty, Flex, List, Modal, Select, Typography } from 'zui/Antd';

const { Text, Title } = Typography;

const steps = [
	{
		id: 1,
		text: 'To update the current device location, click and drag the pin within the map.'
	},
	{
		id: 2,
		text: 'To add a new location, select the Floor and click on the new pin from the Top-Right corner.'
	},
	{
		id: 3,
		text: 'To remove the new location, click on the pin itself to remove it from the map.'
	}
];

const EditDeviceLocationModal = ({
	children,
	editVisible,
	setEditVisible,
	deviceID,
	orgID,
	getControlLocksAndGateways,
	orgName
}) => {
	const dispatch = useDispatch();
	const {
		token: {
			paddingXL,
			fontWeightStrong,
			paddingXS,
			paddingSM,
			fontSize,
			controlItemBgActiveDisabled,
			colorText,
			fontSizeSM,
			colorTextLabel,
			paddingLG,
			colorBorder
		}
	} = theme.useToken();
	const {
		remoteConfig: { constants }
	} = useSelector((state) => state.FbRemoteConfig);

	const [loading, setLoading] = useState(true);
	const [device, setDevice] = useState({});
	const [floors, setFloors] = useState([]);
	const [selectedFloor, setSelectedFloor] = useState({});
	const [mapUrl, setMapUrl] = useState('');
	const [markersData, setMarkersData] = useState([]);
	const [updateLocationLoading, setUpdateLocationLoading] = useState(false);
	const [updateDisable, setUpdateDisable] = useState(true);
	const [marker, setMarker] = useState({});

	// Fetch device and floorplan data
	const fetchDeviceAndFloors = useCallback(async () => {
		setLoading(true);
		try {
			const floorsRes = await SuperFetch.get('/floors', {}, {}, orgID);
			if (floorsRes.statusCode !== 200) {
				Notification('error', 'Error Loading Floors.');
				setLoading(false);
				return;
			}
			const allFloors = floorsRes.data;
			setFloors(allFloors);

			const [deviceRes, locationRes] = await Promise.all([
				SuperFetch.get(`/devices/${deviceID}`, {}, {}, orgID),
				SuperFetch.get(`/devices/${deviceID}/location`, {}, {}, orgID)
			]);

			if (deviceRes.statusCode !== 200 || !deviceRes.data?.length) {
				Notification('error', 'Error Loading Device Information.');
				setLoading(false);
				return;
			}

			const deviceData = deviceRes.data[0];
			const location = locationRes.statusCode === 200 ? locationRes.data : [];

			deviceData.location = location;
			setDevice(deviceData);

			if (location.length > 0) {
				const firstLocation = location[0];
				const floor = allFloors.find((f) => String(f.floorID) === String(firstLocation.floorID));
				if (floor) {
					setSelectedFloor(floor);
					getDownloadURL(floor.url).then((url) => setMapUrl(url));
					setMarkersData([
						{
							x: firstLocation.xAxis,
							y: firstLocation.yAxis,
							defaultCategoryID: firstLocation.defaultCategoryID
						}
					]);
				} else {
					setSelectedFloor({});
					setMarkersData([]);
				}
			} else {
				setSelectedFloor(allFloors[0] || {});
				setMarkersData([]);
			}
		} catch (err) {
			Notification('error', 'Unexpected error loading data.');
		} finally {
			setLoading(false);
		}
	}, [orgID, deviceID]);

	useEffect(() => {
		if (editVisible) fetchDeviceAndFloors();
		return () => {
			setFloors([]);
			setDevice({});
			setMarker({});
		};
	}, [editVisible, fetchDeviceAndFloors]);

	useEffect(() => {
		if (!editVisible || !device || !selectedFloor?.floorID) return;
		const selectedLocation = device.location?.find((l) => l.floorID === selectedFloor.floorID);
		if (selectedLocation) {
			setMarkersData([
				{ x: selectedLocation.xAxis, y: selectedLocation.yAxis, defaultCategoryID: device?.defaultCategoryID }
			]);
		} else {
			setMarkersData([]);
		}
	}, [editVisible, device, selectedFloor]);

	useEffect(() => {
		if (!editVisible || !selectedFloor?.floorID || !marker?.lat || !marker?.lng) {
			setUpdateDisable(true);
			return;
		}
		const original = markersData?.[0];
		if (!original?.x || !original?.y) {
			setUpdateDisable(false);
			return;
		}
		const hasChanged = Number(marker.lat) !== Number(original.y) || Number(marker.lng) !== Number(original.x);
		setUpdateDisable(!hasChanged);
	}, [editVisible, selectedFloor, marker, markersData]);

	useEffect(() => {
		if (selectedFloor?.url && selectedFloor.url !== 'N/A') {
			getDownloadURL(selectedFloor.url)
				.then((url) => {
					if (url !== mapUrl) setMapUrl(url);
				})
				.catch(() => setMapUrl(''));
		} else {
			setMapUrl('');
		}
	}, [mapUrl, selectedFloor.url]);

	const loadInventory = (categoryID) => {
		if (categoryID === constants.SMART_LOCKS) {
			dispatch(inventoryActions.loadInventoryLocks());
		} else if (categoryID === constants.SMART_GATEWAYS) {
			dispatch(inventoryActions.loadInventoryGateways());
		} else {
			getControlLocksAndGateways();
		}
	};

	const handleCancel = () => {
		setEditVisible(false);
		setUpdateLocationLoading(false);
	};

	const onFinish = () => {
		if (updateLocationLoading) return;
		setUpdateLocationLoading(true);
		if (!marker?.lat || !marker?.lng) {
			setUpdateLocationLoading(false);
			return Notification('error', 'Please drop a marker on the map to save location.');
		}
		const locationBody = {
			mapID: selectedFloor.mapID,
			xAxis: marker.lng,
			yAxis: marker.lat,
			floorID: selectedFloor.floorID
		};

		if (device.location.length > 0) {
			SuperFetch.patch(
				`/devices/${device.deviceID}/location/${device.location[0]?.locationID}`,
				locationBody
			).then((res) => {
				if (res.statusCode === 200) {
					Notification('success', 'Device Location Updated.');
					loadInventory(device?.defaultCategoryID);
				} else {
					Notification('error', 'Device Location Update Failed.');
				}
				handleCancel();
			});
		} else {
			SuperFetch.post(`/devices/${device.deviceID}/location`, locationBody).then((res) => {
				if (res.statusCode === 200 || res.statusCode === 201) {
					Notification('success', 'Device Location Created.');
					loadInventory(device?.defaultCategoryID);
				} else {
					Notification('error', 'Device Location Create Failed.');
				}
				handleCancel();
			});
		}
	};

	return (
		<Fragment>
			{children}
			<Modal
				title={null}
				open={editVisible}
				onCancel={handleCancel}
				destroyOnClose
				centered
				width={536}
				footer={[
					<Flex vertical gap={paddingSM}>
						<Button
							disabled={updateDisable}
							block
							onClick={onFinish}
							type='primary'
							loading={updateLocationLoading}
						>
							Save Changes
						</Button>
						<Button key='cancel' onClick={handleCancel} block>
							Cancel
						</Button>
					</Flex>
				]}
				styles={{
					content: {
						padding: '48px'
					}
				}}
			>
				{/* Title and Steps */}
				<Title level={4} style={{ margin: 0, marginBottom: paddingXL }}>
					Update Device Location
				</Title>
				<Title
					level={5}
					style={{
						margin: 0,
						fontWeight: fontWeightStrong,
						marginBottom: paddingSM
					}}
				>
					How it works:
				</Title>

				<List
					itemLayout='horizontal'
					dataSource={steps}
					renderItem={(item) => (
						<List.Item style={{ padding: 0, border: 0, marginBottom: paddingXS }}>
							<List.Item.Meta
								avatar={
									<Avatar
										style={{
											backgroundColor: controlItemBgActiveDisabled,
											color: colorText,
											fontWeight: fontWeightStrong,
											fontSize: fontSize
										}}
										size={24}
									>
										{item.id}
									</Avatar>
								}
								title={
									<Text
										style={{
											fontSize: fontSizeSM,
											fontWeight: 400,
											color: colorTextLabel,
											lineHeight: '20px'
										}}
									>
										{item.text}
									</Text>
								}
							/>
						</List.Item>
					)}
				/>

				{/* Site Info */}
				<Flex vertical style={{ margin: `${paddingLG}px 0px` }}>
					<Title level={5} style={{ marginTop: 0 }}>
						Site
					</Title>
					<Text>{selectedFloor?.siteName ? orgName + ' - ' + selectedFloor?.siteName : <Loader />}</Text>
				</Flex>

				{/* Floor Selector */}
				<Flex vertical style={{ margin: `${paddingLG}px 0px` }}>
					<Title level={5} style={{ marginTop: 0 }}>
						Select Floor
					</Title>
					<Select
						value={selectedFloor.floorID}
						onChange={(floorID) => {
							const floor = floors.find((f) => f.floorID === floorID);
							if (floor) {
								setSelectedFloor(floor);
								setMarkersData([]);
							}
						}}
						style={{ width: '100%' }}
						placeholder='Select a floor'
						options={floors.map((floor) => ({
							label: floor.name,
							value: floor.floorID
						}))}
						filterOption={(input, option) =>
							String(option?.label ?? '')
								.toLowerCase()
								.includes(input.toLowerCase()) ||
							String(option?.value ?? '')
								.toLowerCase()
								.includes(input.toLowerCase())
						}
						showSearch
					/>
				</Flex>

				{/* Map */}
				<Title
					strong
					style={{
						marginTop: paddingLG
					}}
					level={5}
				>
					Position Device on Floorplan
				</Title>

				{loading ? (
					<Loader />
				) : (
					<div
						style={{
							height: 300,
							position: 'relative',
							border: `1px dashed ${colorBorder}`,
							borderRadius: 6,
							overflow: 'hidden',
							marginBottom: 50,
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center'
						}}
					>
						{mapUrl ? (
							<LeafletMap
								mapHeight={300}
								mapWidth={600}
								scrollWheelZoom
								url={mapUrl}
								setMarker={setMarker}
								editMarkerLocation={markersData}
								draggableMarkerLocation={markersData.length > 0}
								drawSingleMarkerEnabled={markersData.length < 1}
								iconType={device.categoryName}
							/>
						) : (
							<Empty description='Please upload a floor plan from the floors page' />
						)}
					</div>
				)}
			</Modal>
		</Fragment>
	);
};

export default EditDeviceLocationModal;
